import { polyfill as polyfillKeyboardEvent } from 'keyboardevent-key-polyfill';
import { Key, normalizeKey } from 'types/keys';

// Enables us to use KeyboardEvent.key in all environments
polyfillKeyboardEvent();

/*
 * This is how we'll define the handlers for keybindings.
 * Here are a few things to note:
 *
 *    1. The isGlobal flag determines whether or not the component (or a descendant) needs
 *       focus in order for the handler to be invoked.
 *    2. To stop propagation on a global handler, either specify stopPropagation: true
 *       or return false in the callback
 */
interface HandlerModel {
  callback: (
    e: React.SyntheticEvent<HTMLElement> | KeyboardEvent,
  ) => void | boolean;
  stopPropagation?: boolean;
  preventDefault?: boolean;
  isGlobal?: boolean;
}

export interface HandlerMap {
  [Key: string]: HandlerModel;
}

/**
 * This method is meant to determine if some content on the page is being edited
 * by the keydown event. If so, we want to prevent global handlers from executing.
 * Consider the following example:
 *
 *   • We have a global handler to focus the search input in the header bound
 *     to the "/" key
 *   • The boards menu popover registers a global handler to close on "Escape"
 *
 * When the boards menu popover is open and its input is focused, we don't want to
 * trigger the "/" handler but we do want to trigger the "Escape" handler.
 */
function isContentEditingEvent(
  evt: React.KeyboardEvent<HTMLElement> | KeyboardEvent,
) {
  const targetElement = evt.target as Element;

  // Element.prototype.matches isn't a thing in IE, so we have to use msMatchesSelector
  const matches = targetElement.matches || targetElement.msMatchesSelector;
  const key = normalizeKey(evt.key);

  return (
    (key.length === 1 || key === Key.Delete || key === Key.Backspace) &&
    (['INPUT', 'TEXTAREA'].includes(targetElement.tagName) ||
      (!!matches && matches.bind(targetElement)('[contenteditable]')))
  );
}

/**
 * Generic callback invoker
 */
export function invokeHandler(
  evt: React.KeyboardEvent<HTMLElement> | KeyboardEvent,
  handler: HandlerModel,
): void | boolean {
  const { stopPropagation, preventDefault, callback, isGlobal } = handler;
  switch (true) {
    // global keybindings should not be triggered if some content is being edited
    case isGlobal && isContentEditingEvent(evt):
      return false;
    case !!stopPropagation:
      evt.stopPropagation();
    case !!preventDefault:
      evt.preventDefault();
    default:
      return callback(evt);
  }
}

/**
 * Holds references to callbacks which are iterated through on keydown
 */
interface GlobalHandlerMap {
  [Key: string]: HandlerModel[];
}

/**
 * Global Keybindings class
 *
 * This is exported as a singleton and maintains a map of global handlers keyed by
 * the KeyboardEvent.key value, binding once in the constructor to the document via
 * document.addEventListener
 */
class GlobalKeybindingsClass {
  handlerMap: GlobalHandlerMap = {};

  constructor() {
    document.addEventListener('keydown', this.handleKeydown);
  }

  destroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  }

  register = (handlerMap: HandlerMap) => {
    for (const key in handlerMap) {
      const handlers = this.handlerMap[key] || [];
      // unshift so that the most recently registered executes first
      handlers.unshift(handlerMap[key]);
      if (!this.handlerMap[key]) {
        this.handlerMap[key] = handlers;
      }
    }
  };

  unregister = (handlerMap: HandlerMap) => {
    for (const key in handlerMap) {
      const handlers = (this.handlerMap[key] || []).filter(
        (handler) => handler.callback !== handlerMap[key].callback,
      );
      !handlers.length
        ? delete this.handlerMap[key]
        : (this.handlerMap[key] = handlers);
    }
  };

  handleKeydown = (evt: KeyboardEvent) => {
    const key = normalizeKey(evt.key);
    const handlers = this.handlerMap[key] || [];

    /**
     * This calls some() here so that we can break early if we are supposed
     * to stop propagation. Note that callbacks bound to other keys will
     * still be invoked.
     */
    handlers.some((handler: HandlerModel) => {
      return invokeHandler(evt, handler) === false || !!handler.stopPropagation;
    });
  };
}

/**
 * Exports a singleton to ensure that we only bind once to the document
 */
export default new GlobalKeybindingsClass();



// WEBPACK FOOTER //
// ./app/common/lib/util/global-keybindings.ts